<template>
    <div class="normal-container">
        <div class="product-slide-track min-h-[300px] lg:min-h-[480px]">
            <LazyUiSlider ref="slider">
                <template v-if="getElements.length > 0">
                    <template v-for="(product, index) in getElements" :key="(product as any)?.id || index">
                        <div
                            class="max-w-[181px] min-w-[181px] lg:max-w-[281px] lg:min-w-[281px]"
                            :class="['keen-slider__slide number-slide' + index]">
                            <div class="max-w-[181px] min-w-[181px] lg:max-w-[281px] lg:min-w-[281px]">
                                <LazyShopwareProduct size="big" :showLabels="showLabels" :product="product" />
                            </div>
                        </div>
                    </template>
                </template>
            </LazyUiSlider>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { useApiClient } from '~/src/apiClient';
    import { shopwareIdsFromItems } from '~/utils/tweakwise';

    const { apiClient } = useApiClient();
    const slider = useTemplateRef('slider');
    const { getFeaturedProducts } = useTweakwise();
    const props = defineProps<{
        showLabels: boolean;
        desktopProductcardSize: string;
        activeTab: any;
        tabIndex: number;
        tabs: any;
        title: string;
        // Sanity _key of the block this slide belongs to; keeps the asyncData key unique per block
        componentId?: string;
    }>();

    onUpdated(() => {
        slider?.value?.update();
    });

    // The asyncData key must be unique per block AND per data source. Two tabs on different
    // pages can share a pageReference but use different Tweakwise recommendations (e.g. the
    // home "Net binnen" tab and an event page tab). With a shared key, client-side navigation
    // reuses the first page's asyncData entry (and handler), so the second page shows the
    // first page's products until a full reload.
    const tab = props.activeTab;
    const asyncDataKey = [
        'product-slide',
        props.componentId || '',
        tab?._key || props.tabIndex,
        tab?.dataSource || 'dpg',
        tab?.dataSource === 'TweakwiseRecommend'
            ? tab?.TweakwiseRecommendationId || ''
            : tab?.pageReference?._ref || '',
    ].join('-');

    const { data: productsData } = await useAsyncData(asyncDataKey, async () => {
        const currentTab = props.activeTab;
        const tabDataSource = currentTab?.dataSource || '';

        switch (tabDataSource) {
            case 'TweakwiseRecommend':
                const tweakwiseId = currentTab?.TweakwiseRecommendationId;
                return await getProductsByTweakwise(tweakwiseId);
            case 'dpg':
            default:
                return await getProductByReference(currentTab?.pageReference?._ref);
        }
    });

    const getElements = computed<any[]>(() => productsData.value || []);

    async function getProductByReference(ref: string) {
        const productResponse = await (apiClient.invoke as any)('getProducts post /product', {
            body: {
                limit: 9,
                filter: [
                    {
                        type: 'equalsAny',
                        field: 'categoryTree',
                        value: ref,
                    },
                    {
                        type: 'equals',
                        field: 'active',
                        value: true,
                    },
                    {
                        type: 'equals',
                        field: 'active',
                        value: true,
                    },
                    {
                        type: 'equals',
                        field: 'parentId',
                        value: null,
                    },
                ],
                sort: [
                    {
                        field: 'createdAt',
                        order: 'desc',
                    },
                ],
            },
            headers: {
                'sw-include-seo-urls': 'true',
            },
        });
        return (productResponse as any)?.data?.elements || [];
    }

    async function getProductsByTweakwise(displayId: any) {
        const recommendationsResponse = await getFeaturedProducts(displayId);
        console.log(recommendationsResponse);

        // Tweakwise returns its own item ids ({crc32hex(domainId)}-{productId}) —
        // Shopware only knows the part after the dash.
        const ids = shopwareIdsFromItems((recommendationsResponse as any)?.items);

        if (ids.length > 0) {
            const productsResponse = await apiClient.invoke('readProduct post /product', {
                body: {
                    filter: [
                        {
                            type: 'equalsAny',
                            field: 'id',
                            value: ids,
                        },
                        {
                            type: 'equals',
                            field: 'active',
                            value: true,
                        },
                    ],
                },
                headers: { 'sw-include-seo-urls': 'true' } as any,
            });

            // Sort products in the same order as the ids array
            const sortedProducts: any[] = [];
            const elements = (productsResponse?.data as any)?.elements || [];

            // Map products by id for efficient lookup
            const productsById: any = {};
            elements.forEach((product: any) => {
                productsById[product.id] = product;
            });

            // Create a new array with products in the same order as ids
            ids.forEach((id: any) => {
                if (productsById[id]) {
                    sortedProducts.push(productsById[id]);
                }
            });

            return sortedProducts;
        }

        return [];
    }
</script>
